import { AuthenticationParameters, Configuration } from 'msal';
import React, { FunctionComponent, useEffect, useState } from 'react';
import {
    AuthenticationState,
    AzureAD,
    IAzureADFunctionProps,
    IMsalAuthProviderConfig,
    LoginType,
    MsalAuthProvider,
} from 'react-aad-msal';
import { AuthenticationConfiguration } from '../../store/modules/site/types';
import { AccessTokenScopes, AuthenticationContext, AuthenticationContextValue } from '../context';

type AuthenticateProps = {
    config: AuthenticationConfiguration;
    userAuthenticated: Function;
};

const Authenticate: FunctionComponent<AuthenticateProps> = (props) => {
    const [authProvider, setAuthProvider] = useState<MsalAuthProvider>();
    const [authScopes, setAuthScopes] = useState<AccessTokenScopes>();

    useEffect(() => {
        if (props.config) {
            const { authProvider: provider, scopes } = createAuthProvider(props.config);

            setAuthProvider(provider);
            setAuthScopes(scopes);
        } else {
            setAuthProvider(undefined);
            setAuthScopes(undefined);
        }
    }, [props.config]);

    if (!authProvider) {
        return null;
    }

    return (
        <AzureAD provider={authProvider} forceLogin>
            {({ authenticationState, accountInfo, logout }: IAzureADFunctionProps) => {
                if (authenticationState === AuthenticationState.Authenticated && accountInfo) {
                    const context: AuthenticationContextValue = {
                        accountInfo,
                        logout: customLogout(authProvider, logout),
                        getAccessToken: authProvider.getAccessToken,
                        scopes: authScopes!, // Always defined if authProvider defined.
                    };

                    props.userAuthenticated(context);

                    return (
                        <AuthenticationContext.Provider value={context}>
                            {props.children}
                        </AuthenticationContext.Provider>
                    );
                }

                return null;
            }}
        </AzureAD>
    );
};

function createAuthProvider(
    config: AuthenticationConfiguration,
): {
    authProvider: MsalAuthProvider;
    scopes: AccessTokenScopes;
} {
    const msalConfig: Configuration = {
        auth: {
            authority: `https://login.microsoftonline.com/${config.tenantId}`,
            clientId: config.clientId,
            redirectUri: `${window.location.origin}/licensing`,
        },
    };

    const parameters: AuthenticationParameters = {};
    const options: IMsalAuthProviderConfig = { loginType: LoginType.Redirect };

    const scopes: AccessTokenScopes = {
        'licenses.read': `https://${config.tenantName}.onmicrosoft.com/licensingservice/licenses.read`,
        'licenses.write': `https://${config.tenantName}.onmicrosoft.com/licensingservice/licenses.write`,
        'licenseTypes.read': `https://${config.tenantName}.onmicrosoft.com/licensingservice/licenseTypes.read`,
        'forms.read': `https://${config.tenantName}.onmicrosoft.com/formservice/forms.read`,
        'inspections.read': `https://${config.tenantName}.onmicrosoft.com/inspectionservice/inspections.read`,
        'inspections.write': `https://${config.tenantName}.onmicrosoft.com/inspectionservice/inspections.write`,
        'violations.read': `https://${config.tenantName}.onmicrosoft.com/violationservice/violations.read`,
        'violations.write': `https://${config.tenantName}.onmicrosoft.com/violationservice/violations.write`,
        'userProfiles.read': `https://${config.tenantName}.onmicrosoft.com/userprofileservice/userProfiles.read-client`,
        'personTypes.read': `https://${config.tenantName}.onmicrosoft.com/licensingservice/personTypes.read`,
        'inspectionTypes.read': `https://${config.tenantName}.onmicrosoft.com/inspectionservice/inspectionTypes.read`,
        'invoiceTypes.read': `https://${config.tenantName}.onmicrosoft.com/accountingservice/invoiceTypes.read`,
        'feeTypes.read': `https://${config.tenantName}.onmicrosoft.com/feeservice/feeTypes.read`,
        'journalEntries.read': `https://${config.tenantName}.onmicrosoft.com/accountingservice/journalEntries.read`,
        'journalEntries.write': `https://${config.tenantName}.onmicrosoft.com/accountingservice/journalEntries.write`,
        'payments.write': `https://${config.tenantName}.onmicrosoft.com/paymentservice/payments.write`,
        'persons.read': `https://${config.tenantName}.onmicrosoft.com/licensingservice/persons.read`,
        'persons.write': `https://${config.tenantName}.onmicrosoft.com/licensingservice/persons.write`,
        'violationCategories.read': `https://${config.tenantName}.onmicrosoft.com/violationservice/violationCategories.read`,
        'events.read': `https://${config.tenantName}.onmicrosoft.com/calendarservice/events.read`,
        'events.write': `https://${config.tenantName}.onmicrosoft.com/calendarservice/events.write`,
        'eventTypes.read': `https://${config.tenantName}.onmicrosoft.com/calendarservice/eventTypes.read`,
        'mergeTemplates.read': `https://${config.tenantName}.onmicrosoft.com/mergetemplateservice/mergeTemplates.read`,
        'timesheets.read': `https://${config.tenantName}.onmicrosoft.com/timesheetservice/timesheets.read`,
        'timesheets.write': `https://${config.tenantName}.onmicrosoft.com/timesheetservice/timesheets.write`,
        'contactTypes.read': `https://${config.tenantName}.onmicrosoft.com/licensingservice/contactTypes.read`,
        'workflows.read': `https://${config.tenantName}.onmicrosoft.com/workflowservice/workflows.read`,
        'citations.read': `https://${config.tenantName}.onmicrosoft.com/citationservice/citations.read`,
        'citations.write': `https://${config.tenantName}.onmicrosoft.com/citationservice/citations.write`,
        'citationTypes.read': `https://${config.tenantName}.onmicrosoft.com/citationservice/citationTypes.read`,
        'applications.read': `https://${config.tenantName}.onmicrosoft.com/formservice/applications.read`,
        'applications.write': `https://${config.tenantName}.onmicrosoft.com/formservice/applications.write`,
    };

    return { authProvider: new MsalAuthProvider(msalConfig, parameters, options), scopes };
}

function customLogout(authProvider: MsalAuthProvider, logout: VoidFunction): VoidFunction {
    return () => {
        // Using the tenant-specific authority doesn't seem to actually log the user out.
        // Use the common authority instead.
        authProvider.authority = 'https://login.microsoftonline.com/common';

        logout();
    };
}

export default Authenticate;
